// Výjimka z optimalizace baterie — JS strana `BatteryOptPlugin` (android/.../BatteryOptPlugin.java).
//
// Bez výjimky uspí správa baterie výrobce i appku s běžící foreground service a GPS se
// pak sbírá jen když ji řidič probudí. Na produkci naměřeno 31. 7. 2026: 88 % výpadků
// polohy delších než 10 minut skončilo do dvou minut po doteku appky.
//
// Samsung (což jsou naši řidiči) má nad Androidem ještě vlastní seznam uspávaných
// aplikací, který systémový dialog NEŘEŠÍ — tam musí řidič ručně, proto `openAppSettings`
// a návod v UI.

import { Capacitor, registerPlugin } from "@capacitor/core";

interface BatteryOptPlugin {
  isIgnoringBatteryOptimizations(): Promise<{ ignoring: boolean }>;
  requestIgnoreBatteryOptimizations(): Promise<{ opened: boolean }>;
  openAppSettings(): Promise<void>;
  deviceInfo(): Promise<{ manufacturer: string; model: string; sdk: number }>;
}

const BatteryOpt = registerPlugin<BatteryOptPlugin>("BatteryOpt");

const isNativeAndroid = () =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";

/** True = appka je osvobozená (nebo běžíme mimo Android, kde pojem nedává smysl). */
export async function isBatteryExempt(): Promise<boolean> {
  if (!isNativeAndroid()) return true;
  try {
    return (await BatteryOpt.isIgnoringBatteryOptimizations()).ignoring;
  } catch {
    return true;   // plugin nedostupný → netrap řidiče hláškou, kterou nevyřeší
  }
}

/** Otevře systémový dialog s žádostí o výjimku. Výsledek se nečeká. */
export async function requestBatteryExempt(): Promise<void> {
  if (!isNativeAndroid()) return;
  try {
    await BatteryOpt.requestIgnoreBatteryOptimizations();
  } catch { /* ignore */ }
}

/** Detail aplikace v nastavení — odsud si řidič dojde na Samsungu do Baterie. */
export async function openAppSettings(): Promise<void> {
  if (!isNativeAndroid()) return;
  try {
    await BatteryOpt.openAppSettings();
  } catch { /* ignore */ }
}

/** Je to Samsung? Podle toho se ukáže návod na jeho vlastní uspávání aplikací. */
export async function isSamsung(): Promise<boolean> {
  if (!isNativeAndroid()) return false;
  try {
    const { manufacturer } = await BatteryOpt.deviceInfo();
    return (manufacturer || "").toLowerCase().includes("samsung");
  } catch {
    return false;
  }
}
